import { useState, useEffect } from "react";
import { useSearchParams } from "react-router-dom";
import { useQueryClient } from "@tanstack/react-query";
import api, { apiErr } from "@/lib/api";
import { toast } from "sonner";
import { useIssues } from "@/lib/useIssues";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Skeleton } from "@/components/ui/skeleton";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { IssueCard } from "@/components/issues/IssueCard";
import { PositionCheck } from "@/components/issues/PositionCheck";
import { RenameTool } from "@/components/issues/RenameTool";
import {
    AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription,
    AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { TransactionDialog } from "@/components/TransactionDialog";
import { SpinoffDialog } from "@/components/issues/SpinoffDialog";
import { CheckCircle2, RefreshCw, Loader2, GitFork } from "lucide-react";

export default function Issues() {
    const qc = useQueryClient();
    const { data, isLoading } = useIssues();
    const [tab, setTab] = useState("open");
    const [editing, setEditing] = useState(null);
    const [inspect, setInspect] = useState("");
    const [rechecking, setRechecking] = useState(false);
    const [deleting, setDeleting] = useState(null);
    const [pendingRename, setPendingRename] = useState(null);
    const [spinoff, setSpinoff] = useState(null);
    const [params] = useSearchParams();
    useEffect(() => {
        const tk = params.get("check");
        if (tk) openInspect(tk.toUpperCase());
    }, [params]); // eslint-disable-line react-hooks/exhaustive-deps

    const removeTxn = async () => {
        try { await api.delete(`/transactions/${deleting.id}`); toast.success("Transaction deleted"); refresh(); }
        catch (e) { toast.error(apiErr(e)); } finally { setDeleting(null); }
    };

    const all = data?.issues || [];
    const shown = all.filter((i) => (tab === "open" ? !i.dismissed : i.dismissed));
    const refresh = () => qc.invalidateQueries();

    const fix = async (payload) => {
        if (payload.action === "rename" && !payload.confirmed) { setPendingRename(payload); return false; }
        if (payload.action === "open_spinoff") { setSpinoff(payload.params); return false; }
        try {
            const { data: r } = await api.post("/issues/fix", payload);
            r.ok === false ? toast.warning(r.message) : toast.success(r.message || "Fixed");
            refresh();
            return r.ok !== false;
        } catch (e) { toast.error(apiErr(e)); return false; }
    };
    const dismiss = async (issue) => {
        try { await api.post("/issues/dismiss", { key: issue.key, restore: issue.dismissed }); refresh(); }
        catch (e) { toast.error(apiErr(e)); }
    };
    const recheckAll = async () => {
        setRechecking(true);
        try { await fix({ action: "recheck_splits", params: {} }); } finally { setRechecking(false); }
    };
    const openInspect = (tk) => {
        setInspect(tk);
        setTimeout(() => document.getElementById("position-check")?.scrollIntoView({ behavior: "smooth" }), 50);
    };

    return (
        <div className="space-y-5" data-testid="issues-page">
            <div className="flex items-end justify-between gap-3 flex-wrap">
                <div>
                    <h1 className="font-heading text-2xl font-semibold">Open issues</h1>
                    <p className="text-sm text-muted-foreground mt-1">Data problems that make Holdings wrong. Use the suggested fix, adjust it yourself, or dismiss it.</p>
                </div>
                <div className="flex gap-2 flex-wrap">
                    <Button variant="outline" size="sm" onClick={() => setSpinoff({})} data-testid="record-spinoff-btn">
                        <GitFork className="w-4 h-4 mr-1.5" />Record spin-off
                    </Button>
                    <Button variant="outline" size="sm" onClick={recheckAll} disabled={rechecking} data-testid="recheck-splits-btn">
                        {rechecking ? <Loader2 className="w-4 h-4 mr-1.5 animate-spin" /> : <RefreshCw className="w-4 h-4 mr-1.5" />}Re-check all splits
                    </Button>
                </div>
            </div>

            <Tabs value={tab} onValueChange={setTab}>
                <TabsList>
                    <TabsTrigger value="open" data-testid="issues-tab-open">Open ({all.filter((i) => !i.dismissed).length})</TabsTrigger>
                    <TabsTrigger value="dismissed" data-testid="issues-tab-dismissed">Dismissed ({all.filter((i) => i.dismissed).length})</TabsTrigger>
                </TabsList>
            </Tabs>

            {isLoading ? <Skeleton className="h-40 rounded-xl" /> : shown.length === 0 ? (
                <Card className="p-8 flex flex-col items-center text-center gap-2" data-testid="issues-empty">
                    <CheckCircle2 className="w-8 h-8 text-gain" />
                    <div className="text-sm font-medium">{tab === "open" ? "No open issues" : "Nothing dismissed"}</div>
                    {tab === "open" && <p className="text-xs text-muted-foreground">Your transactions add up. If a holding still looks wrong, check it below.</p>}
                </Card>
            ) : (
                <div className="space-y-2.5" data-testid="issues-list">
                    {shown.map((i) => <IssueCard key={i.key} issue={i} onFix={fix} onDismiss={dismiss} onEdit={setEditing} onInspect={openInspect} onDelete={setDeleting} />)}
                </div>
            )}

            <PositionCheck ticker={inspect} onEdit={setEditing} />
            <RenameTool onFix={fix} />

            <SpinoffDialog open={!!spinoff} onOpenChange={(o) => !o && setSpinoff(null)} prefill={spinoff} onSaved={refresh} />

            <AlertDialog open={!!pendingRename} onOpenChange={(o) => !o && setPendingRename(null)}>
                <AlertDialogContent data-testid="issue-rename-dialog">
                    <AlertDialogHeader>
                        <AlertDialogTitle>Rename {pendingRename?.params.old} to {pendingRename?.params.new}?</AlertDialogTitle>
                        <AlertDialogDescription>Every {pendingRename?.params.old} transaction, dividend, note and tax rate moves to {pendingRename?.params.new}. The original ticker is kept on each transaction for reference.</AlertDialogDescription>
                    </AlertDialogHeader>
                    <AlertDialogFooter>
                        <AlertDialogCancel data-testid="issue-rename-cancel">Cancel</AlertDialogCancel>
                        <AlertDialogAction onClick={() => { const p = pendingRename; setPendingRename(null); fix({ ...p, confirmed: true }); }} data-testid="issue-rename-confirm">Rename</AlertDialogAction>
                    </AlertDialogFooter>
                </AlertDialogContent>
            </AlertDialog>

            <AlertDialog open={!!deleting} onOpenChange={(o) => !o && setDeleting(null)}>
                <AlertDialogContent data-testid="issue-delete-dialog">
                    <AlertDialogHeader>
                        <AlertDialogTitle>Delete this transaction?</AlertDialogTitle>
                        <AlertDialogDescription>
                            {deleting && `${deleting.transaction_type} ${deleting.quantity} ${deleting.ticker} @ ${deleting.price} · ${deleting.sub_portfolio} · ${deleting.transaction_date}`}. This can't be undone.
                        </AlertDialogDescription>
                    </AlertDialogHeader>
                    <AlertDialogFooter>
                        <AlertDialogCancel data-testid="issue-delete-cancel">Cancel</AlertDialogCancel>
                        <AlertDialogAction onClick={removeTxn} className="bg-loss hover:bg-loss/90" data-testid="issue-delete-confirm">Delete</AlertDialogAction>
                    </AlertDialogFooter>
                </AlertDialogContent>
            </AlertDialog>

            <TransactionDialog open={!!editing} onOpenChange={(o) => !o && setEditing(null)} editing={editing} onSaved={refresh} />
        </div>
    );
}
